import { useCallback } from 'react';
import { ActivityIndicator, Pressable, StyleSheet } from 'react-native';
import { Box } from '../../../components/primitives/Box';
import { Divider } from '../../../components/primitives/Divider';
import { HStack, VStack } from '../../../components/primitives/Stack';
import { Text } from '../../../components/primitives/Text';
import { Toggle } from '../../../components/primitives/Toggle';
import { useAuth } from '../../../contexts/AuthContext';
import { useTranslation } from '../../../contexts/TranslationContext';
import type { NotificationPreference } from '../../../lib/api';
import { colors } from '../../../theme/colors';
import {
  useNotificationPreferences,
  useUpdatePreferences,
} from '../../notifications/hooks/useNotificationPreferences';
import { SettingsSection } from './SettingsSection';

const DIGEST_OPTIONS = ['immediate', 'daily', 'weekly'] as const;

interface ToggleProps {
  label: string;
  description: string;
  value: boolean;
  onValueChange: (val: boolean) => void;
}

function NotificationToggle({ label, description, value, onValueChange }: Readonly<ToggleProps>) {
  return (
    <HStack justify="between" align="center" style={styles.toggleRow}>
      <VStack spacing="xs" style={styles.toggleInfo}>
        <Text variant="body" color={colors.text.primary} style={styles.toggleLabel}>
          {label}
        </Text>
        <Text variant="caption" color={colors.text.tertiary}>
          {description}
        </Text>
      </VStack>
      <Toggle value={value} onValueChange={onValueChange} accessibilityLabel={label} />
    </HStack>
  );
}

interface DigestSelectorProps {
  value: string;
  onChange: (val: 'immediate' | 'daily' | 'weekly') => void;
}

function DigestSelector({ value, onChange }: Readonly<DigestSelectorProps>) {
  const { t } = useTranslation();

  return (
    <HStack spacing="sm" align="center" style={styles.digestRow}>
      <Text variant="caption" color={colors.text.tertiary}>
        {t('notifications.emailDigest')}:
      </Text>
      {DIGEST_OPTIONS.map((opt) => (
        <Pressable
          key={opt}
          onPress={() => onChange(opt)}
          accessibilityRole="button"
          accessibilityState={{ selected: value === opt }}
          accessibilityLabel={t(`notifications.digest.${opt}`)}
        >
          <Box
            backgroundColor={value === opt ? colors.primary.muted : colors.background.tertiary}
            borderRadius="md"
            paddingY="xs"
            paddingX="sm"
          >
            <Text
              variant="caption"
              color={value === opt ? colors.primary[500] : colors.text.tertiary}
              style={styles.digestOption}
            >
              {t(`notifications.digest.${opt}`)}
            </Text>
          </Box>
        </Pressable>
      ))}
    </HStack>
  );
}

function CategoryCard({
  pref,
  onChange,
}: Readonly<{
  pref: NotificationPreference;
  onChange: (updated: NotificationPreference) => void;
}>) {
  const { t } = useTranslation();
  const categoryKey = pref.category;

  return (
    <VStack spacing="md">
      <Text variant="body" color={colors.text.primary} style={styles.categoryTitle}>
        {t(`notifications.categories.${categoryKey}`)}
      </Text>

      <NotificationToggle
        label={t('notifications.channels.email')}
        description={t('notifications.channels.emailDesc')}
        value={pref.emailEnabled}
        onValueChange={(v) => onChange({ ...pref, emailEnabled: v })}
      />

      {pref.emailEnabled && (
        <DigestSelector
          value={pref.emailDigest}
          onChange={(v) => onChange({ ...pref, emailDigest: v })}
        />
      )}
    </VStack>
  );
}

export function NotificationsSection() {
  const { t } = useTranslation();
  const { currentOrganization } = useAuth();
  const orgId = currentOrganization?.id;

  const { data: preferences, isLoading } = useNotificationPreferences(orgId);
  const updatePrefs = useUpdatePreferences();

  const handleChange = useCallback(
    (updated: NotificationPreference) => {
      if (!orgId || !preferences) return;

      const newPrefs = preferences.map((p) => (p.category === updated.category ? updated : p));

      updatePrefs.mutate({ organizationId: orgId, preferences: newPrefs });
    },
    [orgId, preferences, updatePrefs],
  );

  if (isLoading) {
    return (
      <Box
        backgroundColor={colors.background.card}
        borderRadius="xl"
        borderWidth={1}
        borderColor={colors.border.subtle}
        padding="xl"
        alignItems="center"
      >
        <ActivityIndicator color={colors.primary[500]} />
      </Box>
    );
  }

  return (
    <SettingsSection title={t('settings.notifications.title')}>
      {(preferences ?? []).map((pref, idx) => (
        <VStack key={pref.category} spacing="md">
          {idx > 0 && <Divider />}
          <CategoryCard pref={pref} onChange={handleChange} />
        </VStack>
      ))}

      {updatePrefs.isError && (
        <Box backgroundColor={colors.status.error.bg} borderRadius="md" padding="md">
          <Text variant="bodySmall" color={colors.status.error.main}>
            {t('settings.notifications.updateError')}
          </Text>
        </Box>
      )}

      <Box backgroundColor={colors.status.info.bg} borderRadius="md" padding="md">
        <Text variant="bodySmall" color={colors.status.info.main}>
          {t('settings.notifications.info')}
        </Text>
      </Box>
    </SettingsSection>
  );
}

const styles = StyleSheet.create({
  categoryTitle: {
    fontWeight: '600',
  },
  toggleRow: {
    paddingVertical: 4,
  },
  toggleInfo: {
    flex: 1,
    minWidth: 0,
    marginRight: 16,
  },
  toggleLabel: {
    fontWeight: '500',
  },
  digestRow: {
    paddingLeft: 8,
  },
  digestOption: {
    fontWeight: '500',
  },
});
